/**
 * Report that a card actually reached the screen.
 *
 * The server cannot know this, and "it was in the response" is not an impression —
 * without a real viewport check the click-through rate has an arbitrary denominator.
 *
 * Fires once per mount. The guard is a ref rather than state so that the dashboard's
 * sixty-second refetch, which re-renders the cards without remounting them, does not
 * record a second impression every minute.
 */

import { useCallback, useEffect, useRef } from "react";

/** Half the card visible is a fair definition of "seen". */
const VISIBLE_RATIO = 0.5;

export function useImpression<T extends HTMLElement>(
  onImpression: (() => void) | undefined,
): (node: T | null) => void {
  const fired = useRef(false);
  const latest = useRef(onImpression);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    latest.current = onImpression;
  });

  useEffect(() => {
    return () => observer.current?.disconnect();
  }, []);

  return useCallback((node: T | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!node || latest.current === undefined) return;
    if (typeof IntersectionObserver === "undefined") return;

    observer.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || fired.current) continue;
          fired.current = true;
          observer.current?.disconnect();
          latest.current?.();
        }
      },
      { threshold: VISIBLE_RATIO },
    );
    observer.current.observe(node);
  }, []);
}
